import { FilterTable } from "@/features/FilterTable/FilterTable";
import { Field } from "@/features/FilterTable/types";
import { createAssetClassFilterFields } from "@/features/FilterTable/assetClassFilterFields";
import { DepField, makeFiltersBuilder } from "@/features/FilterTable/utils";
import {
  dateSchema,
  parseToDateValue,
  preprocessTextAsyncAutocompleteField,
} from "@/features/FormCard/utils";
import { createCurrencyOption } from "@/hooks/useCurrencyOption";
import { Clients } from "@/lib/clients";
import { Tables } from "@/gen/tableNames/tables";
import { z } from "zod";
import { formFields } from "./edit_card";
import { useTranslation } from "react-i18next";

const filterN1TimeSeriesSchema = z.object({
  n1_static_datum_name_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional(),
  isin_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional(),
  currency_contains: z.string().optional(),
  date_from: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
  date_to: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
  create_from: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
  create_to: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
  asset_class_1_equals: z.string().optional(),
  asset_class_2_equals: z.string().optional(),
});

const filterN1TimeSeriesFields = filterN1TimeSeriesSchema.keyof().enum;

export type FilterN1TimeSeriesSchema = z.infer<typeof filterN1TimeSeriesSchema>;

const assetDepFields: readonly DepField<FilterN1TimeSeriesSchema>[] = [
  {
    name: filterN1TimeSeriesFields.n1_static_datum_name_contains,
    key: "id",
    extract: "id",
    bucket: "equals",
  },
  {
    name: filterN1TimeSeriesFields.isin_contains,
    key: "isin",
    extract: "text",
    bucket: "equals",
  },
];

export function FilterN1TimeSeries() {
  const { t, i18n } = useTranslation("tables");
  const tableName = Tables.N1_TIME_SERIES;

  const fields: Field[] = [
    {
      name: filterN1TimeSeriesFields.n1_static_datum_name_contains,
      label: t(`${tableName}.searchbox.n1_static_datum_name_contains`, { defaultValue: "Asset" }),
      type: "asyncAutocomplete",
      base_url: `${Clients.N1}/${Tables.N1_STATIC_DATA}`,
      id_column: formFields.n1_static_datum_id,
      column_name: "name",
      col_span: 2,
      filters: makeFiltersBuilder<FilterN1TimeSeriesSchema>(
        assetDepFields,
        filterN1TimeSeriesFields.n1_static_datum_name_contains,
      ),
    },
    ...createAssetClassFilterFields({
      assetClassFieldName: filterN1TimeSeriesFields.asset_class_1_equals,
      assetSubclassFieldName: filterN1TimeSeriesFields.asset_class_2_equals,
      assetClassLabel: t(`${Tables.N1_TIME_SERIES}.filter.asset_class_1`),
      assetSubclassLabel: t(`${Tables.N1_TIME_SERIES}.filter.asset_class_2`),
      client: Clients.N1,
      staticDataTableName: Tables.N1_STATIC_DATA,
      language: i18n.resolvedLanguage?.startsWith("fr") ? "fr" : "en",
      assetSubclassFieldType: "select",
    }),
    {
      name: filterN1TimeSeriesFields.isin_contains,
      label: t(`${tableName}.searchbox.isin_contains`, { defaultValue: "ISIN" }),
      type: "asyncAutocomplete",
      base_url: `${Clients.N1}/${Tables.N1_STATIC_DATA}`,
      id_column: formFields.n1_static_datum_id,
      column_name: "isin",
      filters: makeFiltersBuilder<FilterN1TimeSeriesSchema>(
        assetDepFields,
        filterN1TimeSeriesFields.isin_contains,
      ),
    },
    {
      name: filterN1TimeSeriesFields.currency_contains,
      label: t(`${tableName}.searchbox.currency_contains`, { defaultValue: "Currency" }),
      type: "autocomplete",
      options: createCurrencyOption(),
    },
    {
      name: "date",
      type: "dateRange",
      label: t(`${tableName}.searchbox.date`, { defaultValue: "Value date" }),
      from: { name: filterN1TimeSeriesFields.date_from },
      to: { name: filterN1TimeSeriesFields.date_to },
      col_span: 2,
    },
    {
      name: "create",
      type: "dateRange",
      label: t(`${tableName}.searchbox.create`, { defaultValue: "Creation date" }),
      from: { name: filterN1TimeSeriesFields.create_from },
      to: { name: filterN1TimeSeriesFields.create_to },
      col_span: 2,
    },
  ];

  return (
    <FilterTable
      schema={filterN1TimeSeriesSchema}
      client={Clients.N1}
      tableName={Tables.N1_TIME_SERIES}
      fields={fields}
      columns={2}
    />
  );
}
